/**
 * StandardsCoverageButton — top-of-dashboard shortcut that shows every
 * standard covered per grade, merged from `daily_worksheets` (K-1 & one-off)
 * and `daily_unified_plans` (the 5–8 daily cross-grade lesson).
 *
 * Backend: GET /api/school/admin/standards-coverage
 * Used for: state compliance reporting, parent conversations, and "what have
 * we actually taught this cohort?" at-a-glance.
 */
import React, { useEffect, useState } from 'react';
import { BookOpen, Download, Loader2, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const GRADE_LABEL = (g) => (g === '0' ? 'K' : g);
const GRADE_TINT = {
  '0': 'border-pink-500/50 bg-pink-500/5 text-pink-300',
  '4': 'border-cyan-500/50 bg-cyan-500/5 text-cyan-300',
  '5': 'border-sky-500/50 bg-sky-500/5 text-sky-300',
  '6': 'border-amber-500/50 bg-amber-500/5 text-amber-300',
  '7': 'border-orange-500/50 bg-orange-500/5 text-orange-300',
  '8': 'border-emerald-500/50 bg-emerald-500/5 text-emerald-300',
  '9': 'border-violet-500/50 bg-violet-500/5 text-violet-300',
};

const StandardsCoverageButton = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeGrade, setActiveGrade] = useState(null);

  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    api
      .get('/school/admin/standards-coverage', { headers: authHeaders() })
      .then((r) => {
        setData(r.data);
        // Default the tab to the grade with the most coverage.
        const grades = Object.keys(r.data.by_grade || {}).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
        setActiveGrade(grades[0] || null);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load coverage'))
      .finally(() => setLoading(false));
  }, [open, data]);

  const close = () => setOpen(false);

  const exportCSV = () => {
    if (!data) return;
    const rows = [['Grade', 'Date', 'Subject', 'Standard', 'Title', 'Source']];
    Object.keys(data.by_grade || {})
      .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
      .forEach((g) => {
        (data.by_grade[g] || []).forEach((r) => {
          rows.push([
            GRADE_LABEL(g),
            r.date || '',
            r.subject || '',
            r.standard_code || '',
            (r.title || '').replace(/"/g, '""'),
            r.source || '',
          ]);
        });
      });
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `saa-standards-coverage-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const grades = data ? Object.keys(data.by_grade || {}).sort((a, b) => parseInt(a, 10) - parseInt(b, 10)) : [];
  const activeRows = activeGrade ? (data?.by_grade?.[activeGrade] || []) : [];

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 border-2 border-sky-500 bg-sky-500 hover:bg-sky-400 text-black px-4 py-2 text-[10px] font-black uppercase tracking-widest"
        data-testid="link-standards-coverage"
      >
        <BookOpen className="w-3.5 h-3.5" /> Standards Covered
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85"
          onClick={close}
          data-testid="standards-coverage-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col bg-zinc-950 border-2 border-sky-500/60"
          >
            {/* Header */}
            <div className="border-b-2 border-sky-500/40 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sky-400">
                <BookOpen className="w-4 h-4" />
                <span className="text-[10px] font-black uppercase tracking-[0.28em]">Standards Coverage</span>
              </div>
              <div className="flex items-center gap-2">
                {data && (
                  <button
                    onClick={exportCSV}
                    className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest border border-sky-500/50 hover:border-sky-400 text-sky-300 px-2 py-1"
                    data-testid="standards-coverage-export"
                  >
                    <Download className="w-3 h-3" /> CSV
                  </button>
                )}
                <button onClick={close} className="text-zinc-400 hover:text-white p-1" data-testid="standards-coverage-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {loading && (
                <div className="flex items-center justify-center py-16 text-zinc-500 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading coverage…
                </div>
              )}

              {!loading && data && (
                <>
                  {/* Summary strip */}
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">
                      Season summary
                      {data.date_range?.first && (
                        <span className="ml-2 text-zinc-600">
                          · {data.date_range.first} → {data.date_range.last} · {data.date_range.days_covered} teaching days
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                      {grades.map((g) => (
                        <button
                          key={g}
                          onClick={() => setActiveGrade(g)}
                          className={`text-left p-3 border-2 transition-colors ${GRADE_TINT[g] || 'border-zinc-700'} ${activeGrade === g ? 'ring-2 ring-white/60' : ''}`}
                          data-testid={`standards-coverage-tab-${g}`}
                        >
                          <div className="text-[9px] uppercase tracking-widest font-black">Grade {GRADE_LABEL(g)}</div>
                          <div className="text-2xl font-black text-white">{data.totals[g]}</div>
                          <div className="text-[10px] text-zinc-400">
                            {data.unique_standards[g]} unique standard{data.unique_standards[g] === 1 ? '' : 's'}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Active grade table */}
                  {activeGrade && (
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">
                        Grade {GRADE_LABEL(activeGrade)} · {activeRows.length} lesson{activeRows.length === 1 ? '' : 's'}
                      </div>
                      <div className="border-2 border-zinc-800 divide-y divide-zinc-800 max-h-[50vh] overflow-y-auto">
                        {activeRows.length === 0 && (
                          <div className="px-3 py-4 text-xs text-zinc-500 italic">
                            No standards covered yet for this grade.
                          </div>
                        )}
                        {activeRows.map((r, i) => (
                          <div key={i} className="px-3 py-2 flex items-start justify-between gap-3 text-sm">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-[10px] font-black tracking-widest uppercase text-zinc-500 tabular-nums">
                                  {r.date}
                                </span>
                                <span className="text-[9px] uppercase tracking-widest text-zinc-600 border border-zinc-800 px-1">
                                  {r.source === 'unified_plan' ? 'Unified 5–8' : 'Per-Grade'}
                                </span>
                              </div>
                              <div className="font-bold text-white truncate">{r.title || r.subject}</div>
                              <div className="text-[11px] text-zinc-500 truncate">{r.subject}</div>
                            </div>
                            <div className="flex-shrink-0">
                              <span className="inline-block bg-sky-500/10 border border-sky-500/40 text-sky-300 px-2 py-1 text-[10px] font-black tracking-widest uppercase whitespace-nowrap">
                                {r.standard_code}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StandardsCoverageButton;
